import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ProductCard, type Product } from '@/components/ui/ProductCard';
import { urlFor } from '@/sanity/client';
import type { ProductsSectionData } from '@/sanity/types';

// R2 - Featured products with images and categories
const FEATURED_PRODUCTS: Product[] = [
  {
    id: 1,
    title: 'Módulos Prefabricados',
    description: 'Soluciones modulares de alta calidad para cualquier proyecto.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1764269542/modulo_home_new_hlbrx7.jpg',
    category: 'Construcción',
    features: ['6m estándar', 'Aislamiento térmico', 'Instalación rápida'],
  },
  {
    id: 2,
    title: 'Módulos Sanitarios',
    description: 'Aseos portátiles con acabados premium y máxima higiene.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__2_ujmieo.jpg',
    category: 'Sanitarios',
    features: ['Alta capacidad', 'Fácil limpieza', 'Diseño moderno'],
  },
  {
    id: 3,
    title: 'WC Químico Portátil',
    description: 'Soluciones compactas y eficientes para eventos y obras.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__2_ujmieo.jpg',
    category: 'Eventos',
    features: ['Compacto', 'Autónomo', 'Ecológico'],
  },
  {
    id: 4,
    title: 'Contenedor Marítimo',
    description: 'Espacios funcionales y seguros para cualquier proyecto constructivo.',
    image: 'https://res.cloudinary.com/dphpfdsk3/image/upload/e_background_removal/f_png/v1765429345/modulo_inboca__1_bvig72.jpg',
    category: 'Construcción',
    features: ['Seguridad', 'Versatilidad', 'Durabilidad'],
  },
];

interface FeaturedProductsSectionProps {
  data?: ProductsSectionData | undefined;
}

/**
 * R2 - Featured products using reusable ProductCard component
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function FeaturedProductsSection({ data }: FeaturedProductsSectionProps) {
  const badge = data?.badge ?? 'Nuestro Catálogo';
  const sectionTitle = data?.title ?? 'Productos Destacados';
  const sectionDesc = data?.description ?? 'Soluciones modulares de primera calidad para cada necesidad';
  const ctaText = data?.ctaText ?? 'Ver Catálogo Completo';

  // R4 - Resolve Sanity products or use hardcoded defaults
  const resolvedProducts: Product[] = data?.items?.length
    ? data.items
        .filter(item => item.isFeatured)
        .map((item, idx) => ({
          id: idx + 1,
          title: item.title,
          description: item.description,
          image: urlFor(item.image).width(600).url(),
          category: item.category,
          features: item.features,
        }))
    : FEATURED_PRODUCTS;
  return (
    <section className="relative py-20 sm:py-32 bg-gradient-to-b from-white to-inboca-gray">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20 animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-inboca-teal/10 rounded-full text-inboca-teal font-semibold text-sm mb-6">
            {badge}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            {sectionTitle}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {sectionDesc}
          </p>
        </div>

        {/* Products Grid - Using reusable ProductCard */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {resolvedProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* CTA to Full Catalog */}
        <div className="mt-20 text-center animate-fade-in-up">
          <div className="inline-flex flex-col items-center gap-6 p-8 bg-white rounded-3xl shadow-lg">
            <p className="text-lg text-gray-700">
              ¿Buscas ver todo nuestro catálogo?
            </p>
            <Link
              to="/productos"
              className="inline-flex items-center gap-3 bg-inboca-teal hover:bg-inboca-teal/90 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {ctaText}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
